import {
  DEFAULT_VALUES,
  MONTHS_IN_SPANISH,
} from 'src/app/shared/common/constants';
import { validate } from 'rut.js';

declare const __webpack_public_path__: string;

export const deviceWidth = () => window.innerWidth;

//target: holamundo, toInsert: foo, position: 4 -> resultado: holafoomundo
export const insertStringAtPosition = (
  target: string,
  toInsert: string,
  position: number
): string => {
  return target.substring(0, position) + toInsert + target.substring(position);
};

//capitalizes the first letter of every word
export const toTitleCase = (phrase: string) => {
  return phrase
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const toTitleCaseOrDefault = (value: string): string => {
  return value ? toTitleCase(value) : DEFAULT_VALUES.NO_DATA_FROM_SERVICE;
};

//eg returns Junio 2023
export const getSpanishNextMonthAsTextPlusYear = () => {
  const now = new Date();
  let nextMonthDate;
  if (now.getMonth() === 11) {
    nextMonthDate = new Date(now.getFullYear() + 1, 0, 1);
  } else {
    nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  const monthKey = nextMonthDate.getMonth() as keyof typeof MONTHS_IN_SPANISH;
  const spanishMonth = MONTHS_IN_SPANISH[monthKey];

  return `${spanishMonth} ${nextMonthDate.getFullYear()}`;
};

export const dateMonthsDifference = (from: Date, to: Date): number => {
  const difference =
    to.getMonth() -
    from.getMonth() +
    12 * (to.getFullYear() - from.getFullYear());

  return Math.max(difference, 0);
};

function getPlural(number: number, word: any) {
  return (number === 1 && word.one) || word.other;
}

export const getSpanishDateDifferenceFormatted = (monthCount: number) => {
  const months = { one: 'mes', other: 'meses' },
    years = { one: 'año', other: 'años' },
    m = monthCount % 12,
    y = Math.floor(monthCount / 12),
    result = [];

  y && result.push(y + ' ' + getPlural(y, years));
  m && result.push(m + ' ' + getPlural(m, months));
  return result.join(' y ');
};

export const getDateformatMonthYearString = (dateToTranslate: Date): string => {
  const monthKey = dateToTranslate.getMonth() as keyof typeof MONTHS_IN_SPANISH;
  const spanishMonth = MONTHS_IN_SPANISH[monthKey];

  return `${spanishMonth} ${dateToTranslate.getFullYear()}`;
};

export const getNextMonthDate = (dateToFormat: Date): Date => {
  const finalDate: Date =
    dateToFormat.getMonth() == 11
      ? new Date(dateToFormat.getFullYear() + 1, 0, 1)
      : new Date(dateToFormat.getFullYear(), dateToFormat.getMonth() + 1, 1);

  return finalDate;
};

export function customRutFormValidator(formGroup: any) {
  const rut = formGroup.value;
  const rutValid = validate(rut);
  return rutValid ? null : { rut: true };
}

export function removeDotsFromText(text: any): string {
  return text.toString().replace(/\./g, '');
}

export function downloadPdf(pdf: string, documentName: string) {
  const link = document.createElement('a');
  link.setAttribute('target', '_blank');
  link.setAttribute('href', `data:application/pdf;base64,${pdf}`);
  link.setAttribute('download', documentName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export const bringModalToFront = (
  element: HTMLElement,
  zIndex: string = '1000000'
): void => {
  const modal = element.closest('ion-modal');
  if (!modal) return;

  document.body.appendChild(modal);
  (modal as HTMLElement).style.setProperty('z-index', zIndex, 'important');
};

export const scrollViewToTop = (element: HTMLElement | null): void => {
  window.scrollTo({ top: 0 });

  let current: HTMLElement | null = element;

  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    const isScrollable =
      (overflow === 'auto' || overflow === 'scroll') &&
      current.scrollHeight > current.clientHeight;

    if (isScrollable) {
      current.scrollTop = 0;
      return;
    }

    current = current.parentElement;
  }
};

export const toAssetUrl = (asset: string): string => {
  const base =
    typeof __webpack_public_path__ === 'string' ? __webpack_public_path__ : '';

  return `${base}assets/apv/${asset}`;
};

const YOUTUBE_HOSTS = ['www.youtube.com', 'youtube.com', 'm.youtube.com', 'youtu.be'];
const YOUTUBE_VIDEO_ID = /^[\w-]{11}$/;

export const toYoutubeEmbedUrl = (url: string): string | null => {
  try {
    const { protocol, hostname, pathname, searchParams } = new URL(url);

    if (protocol !== 'https:' || !YOUTUBE_HOSTS.includes(hostname)) return null;

    const id =
      hostname === 'youtu.be'
        ? pathname.slice(1)
        : searchParams.get('v') ??
          /^\/(?:embed|shorts)\/([^/]+)/.exec(pathname)?.[1];

    return id && YOUTUBE_VIDEO_ID.test(id)
      ? `https://www.youtube.com/embed/${id}`
      : null;
  } catch {
    return null;
  }
};
